/**
 * №22.2 (D-CV1) — the answer RECEIPT bookkeeping for LIST surfaces (home · desk ·
 * matter). Answering is an EVENT WITH A RECEIPT, never a disappearance: when a decision
 * card is answered in a list, its snapshot is kept HERE so the card renders RESOLVED IN
 * PLACE (a one-line receipt) even after the pending query drops it.
 *
 * Page-local by design: the map lives in the surface's own state, so it resets when the
 * surface unmounts. That is exactly the D-CV1 rule — the receipt "fades on next visit,
 * never instantly": the surface you answered on holds the receipt until you leave; the
 * OTHER surfaces clear immediately (D-CC4, via the mutation's invalidations).
 */
import { useState, useCallback } from "react";
import type { NeedsYouOption, NeedsYouEvidence } from "../../lib/types";

export interface AnsweredReceipt {
  optionIndex: number;
  label: string;
  at: string;
  outcome: "executed" | "failed" | "skipped";
  /** A snapshot of the card so the receipt still renders after the task leaves the query. */
  reason: string;
  options: NeedsYouOption[];
  evidence: NeedsYouEvidence[];
  agentName?: string;
  anchorLabel?: string;
  /**
   * №22.5 (F1) — WHICH QUESTION this receipt is for. A receipt is keyed by task, but a
   * BATCHED task outlives the question that was answered: the card advances and the
   * same task id immediately carries a different, live question. Without this the
   * receipt shadowed it, and the desk rendered an inert "✓ Resolved" over a question
   * that was waiting to be answered — with the section heading counting 0.
   * Undefined for an unbatched card, which is the common case and compares equal.
   */
  batchIndex?: number;
  /** №22.5 (C4) — what a skip recorded, so the receipt states it (never a bare ⊘). */
  consequence?: string;
}

/**
 * №22.5 (F1) — does this receipt still describe the card the task is showing?
 *
 * ONE rule, in one place, because all three list surfaces (desk · Needs-You home ·
 * matter rail) need it and a per-surface copy is the second implementation D-CC4
 * forbids. Both branches matter:
 *
 *   - SAME question (indexes equal, or both absent) → the receipt shadows the live row.
 *     That is D-CV1, and it is also what covers the moment between answering and the
 *     pending query refetching: without it the card would flash back as clickable and
 *     invite a second answer.
 *   - DIFFERENT question (the batch advanced) → **null**. There is a real, unanswered
 *     question on that task now, and a receipt over it is worse than a vanish: it looks
 *     handled AND it cannot be clicked.
 */
export function receiptFor(
  receipt: AnsweredReceipt | undefined,
  task: { needs_you_batch?: { index: number; total: number } } | null | undefined,
): AnsweredReceipt | null {
  if (!receipt) return null;
  return receipt.batchIndex === task?.needs_you_batch?.index ? receipt : null;
}

export function useJustAnswered() {
  const [map, setMap] = useState<Record<string, AnsweredReceipt>>({});
  const record = useCallback((taskId: string, receipt: AnsweredReceipt) => {
    setMap((m) => ({ ...m, [taskId]: receipt }));
  }, []);
  /** The batch advanced past a recorded receipt → drop it, so the row is live again. */
  const clear = useCallback((taskId: string) => {
    setMap((m) => { if (!(taskId in m)) return m; const next = { ...m }; delete next[taskId]; return next; });
  }, []);
  return { map, record, clear };
}
